import { Injectable } from '@angular/core';
import { State, Action, StateContext, Selector } from '@ngxs/store';
import { SeasonalDetailService } from '../../core/services/seasonal-detail/seasonal-detail.service';
import {
  IGetDetailHolyWeek,
  IGetDetailJuneVacation,
  IGetDetailEndYear,
} from '../../core/interfaces/detail-seasonal.interface';
import { tap } from 'rxjs/operators';
import {
  GetHolyWeekDetail,
  GetJuneVacationsDetail,
  GetRecessWeekDetail,
  GetEndYearDetail,
  SaveIgnoredDates,
} from './seasonal-detail.actions';
import { IgnoredDatesForm } from '@/app/modules/home/pages/prediction/seasonal-detail/detail-holy-week/detail-holy-week.component';

export interface SeasonalDetailStateModel {
  holyWeekDetail: IGetDetailHolyWeek | null;
  recessWeekDetail: IGetDetailHolyWeek | null;
  juneVacationsDetail: IGetDetailJuneVacation | null;
  endYearDetail: IGetDetailEndYear | null;
  loading: boolean;
  error: any;
  ignoredDates: IgnoredDatesForm | null;
}

@State<SeasonalDetailStateModel>({
  name: 'seasonalDetail',
  defaults: {
    holyWeekDetail: null,
    recessWeekDetail: null,
    juneVacationsDetail: null,
    endYearDetail: null,
    loading: false,
    error: null,
    ignoredDates: null,
  },
})
@Injectable()
export class SeasonalDetailState {
  constructor(private seasonalDetailService: SeasonalDetailService) {}

  @Selector()
  static getHolyWeekDetail(state: SeasonalDetailStateModel) {
    return state.holyWeekDetail;
  }

  @Selector()
  static getLoading(state: SeasonalDetailStateModel) {
    return state.loading;
  }

  @Selector()
  static getError(state: SeasonalDetailStateModel) {
    return state.error;
  }

  @Selector()
  static getRecessWeekDetail(state: SeasonalDetailStateModel) {
    return state.recessWeekDetail;
  }

  @Selector()
  static getJuneVacationsDetail(state: SeasonalDetailStateModel) {
    return state.juneVacationsDetail;
  }

  @Selector()
  static getEndYearDetail(state: SeasonalDetailStateModel) {
    return state.endYearDetail;
  }

  @Selector()
  static getIgnoredDates(state: SeasonalDetailStateModel) {
    return state.ignoredDates;
  }

  @Action(GetHolyWeekDetail)
  getHolyWeekDetail(
    ctx: StateContext<SeasonalDetailStateModel>,
    action: GetHolyWeekDetail
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.seasonalDetailService
      .getSeasonalDetailHolyWeek(action.payload)
      .pipe(
        tap({
          next: response => {
            ctx.patchState({
              holyWeekDetail: response,
              loading: false,
            });
          },
          error: error => {
            ctx.patchState({
              error,
              loading: false,
            });
          },
        })
      );
  }

  @Action(GetRecessWeekDetail)
  getRecessWeekDetail(
    ctx: StateContext<SeasonalDetailStateModel>,
    action: GetRecessWeekDetail
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.seasonalDetailService
      .getSeasonalDetailRecessWeek(action.payload)
      .pipe(
        tap({
          next: response => {
            ctx.patchState({
              recessWeekDetail: response,
              loading: false,
            });
          },
          error: error => {
            ctx.patchState({
              error,
              loading: false,
            });
          },
        })
      );
  }

  @Action(GetJuneVacationsDetail)
  getJuneVacationsDetail(
    ctx: StateContext<SeasonalDetailStateModel>,
    action: GetJuneVacationsDetail
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.seasonalDetailService
      .getSeasonalDetailJuneVacations(action.payload)
      .pipe(
        tap({
          next: response => {
            ctx.patchState({
              juneVacationsDetail: response,
              loading: false,
            });
          },
          error: error => {
            ctx.patchState({
              error,
              loading: false,
            });
          },
        })
      );
  }

  @Action(GetEndYearDetail)
  getEndYearDetail(
    ctx: StateContext<SeasonalDetailStateModel>,
    action: GetEndYearDetail
  ) {
    ctx.patchState({ loading: true, error: null });
    return this.seasonalDetailService
      .getSeasonalDetailEndYear(action.payload)
      .pipe(
        tap({
          next: response => {
            ctx.patchState({
              endYearDetail: response,
              loading: false,
            });
          },
          error: error => {
            ctx.patchState({
              error,
              loading: false,
            });
          },
        })
      );
  }

  @Action(SaveIgnoredDates)
  saveIgnoredDates(
    ctx: StateContext<SeasonalDetailStateModel>,
    action: SaveIgnoredDates
  ) {
    ctx.patchState({ ignoredDates: action.payload.ignoredDates });
  }
}
